import React from "react";
import { Navigate } from "react-router-dom";
import { UserLevel } from "common/userLevels";
import { PERMISSIONS } from "common/permissions";
import { AUTH_URL } from "common/apiUrls";

// ─── Token / session helpers ─────────────────────────────────────────────────

export function isLoggedIn() {
  return !!localStorage.getItem("token");
}
export function setLoggedIn(token) {
  localStorage.setItem("token", token);
}
export function getToken() {
  return localStorage.getItem("token");
}
export function getUserId() {
  return localStorage.getItem("userId");
}
export function setLoggedInUser(user) {
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("firstTime", user.firstTime);
}
export function getLoggedInUser() {
  const data = localStorage.getItem("user");
  return JSON.parse(data);
}
export const getLoggedInName = () =>
  String((getLoggedInUser() || {}).name || "").trim();
export function isUserFirstTime() {
  return localStorage.getItem("firstTime");
}
export function setLoggedInUserLevel(userLevel) {
  localStorage.setItem("userLevel", userLevel);
}
export function getLoggedInUserLevel() {
  const stored = parseInt(localStorage.getItem("userLevel"));
  if (stored) return stored;
  const user = getLoggedInUser();
  return user && user.level ? user.level : 0;
}

// ─── Role checks ─────────────────────────────────────────────────────────────

function levelIs(level) {
  return getLoggedInUserLevel() === level;
}

export const isCustomer = () => levelIs(UserLevel.CUSTOMER);
export const isCounterStaff = () => levelIs(UserLevel.COUNTER_STAFF);
// The dashboard and its pages: the super admin (by permission from the server).
export const isAdmin = () => hasPermission(PERMISSIONS.DASHBOARD);
export const isOrderChecker = () => levelIs(UserLevel.ORDER_CHECKER);
export const isBaker = () => levelIs(UserLevel.BAKER);
export const isCashier = () => levelIs(UserLevel.CASHIER);
export const isDelivery = () => levelIs(UserLevel.DELIVERY);
export const isOutletManager = () => levelIs(UserLevel.OUTLET_MANAGER);
export const isWaiter = () => levelIs(UserLevel.WAITER);
export const isSuperAdmin = () => levelIs(UserLevel.SUPER_ADMIN);

// Legacy aliases kept so existing screens don't break
export const isUser = isCustomer;
export const isRetailer = isCounterStaff;
export const isChecker = isOrderChecker;

// ─── Permissions ─────────────────────────────────────────────────────────────
// From the server at login and on every page load (refreshUser). Changing
// them in the browser only changes what's shown: the server checks again.

export function getPermissions() {
  const user = getLoggedInUser();
  return (user && Array.isArray(user.permissions) && user.permissions) || [];
}
export function hasPermission(...perms) {
  const mine = getPermissions();
  return perms.some((p) => mine.includes(p));
}
export const isStaffUser = () => getPermissions().length > 0;

// ─── Navigation helpers ───────────────────────────────────────────────────────

// Where someone lands after logging in.
export function getDefaultPathForLevel() {
  if (!isLoggedIn()) return "/";
  if (hasPermission(PERMISSIONS.DASHBOARD)) return "/dashboard";
  if (hasPermission(PERMISSIONS.QUICK_ORDER)) return "/quick-order";
  if (hasPermission(PERMISSIONS.QUEUE_ADD)) return "/token";
  // delivery staff (who don't bake) start at what's ready to deliver
  if (
    hasPermission(PERMISSIONS.DELIVERY) &&
    !hasPermission(PERMISSIONS.BAKE_UPDATE)
  ) {
    return "/deliveries";
  }
  if (hasPermission(PERMISSIONS.BAKE_BOARD)) return "/bake";
  if (hasPermission(PERMISSIONS.ORDER_CHECK)) return "/order-checker";
  return "/buy-cake";
}

// Where "My account" and "My orders" links go.
export function getAccountPath() {
  return isStaffUser() ? getDefaultPathForLevel() : "/orders";
}

// ─── User storage ─────────────────────────────────────────────────────────────

export function setUser(user) {
  if (user.token) localStorage.setItem("token", user.token);
  if (user.level) localStorage.setItem("userLevel", user.level);
  if (user.userId) localStorage.setItem("userId", user.userId);
  if (user.isFirstTime) localStorage.setItem("firstTime", user.firstTime);
  localStorage.setItem("user", JSON.stringify(user));
}

// Reloads the logged-in user (role, permissions) from the server, so a
// changed role or a disabled account takes effect on the next page load.
// Returns the user, or null when the session has ended (then logged out).
export async function refreshUser() {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch(`${AUTH_URL}/me`, {
      headers: { "x-auth-token": token },
    });
    if (res.status === 401) {
      resetUser();
      return null;
    }
    const data = await res.json();
    if (data && data.result) {
      setUser({ ...data.result, token });
      return data.result;
    }
  } catch (e) {
    // offline: keep what we have
  }
  return getLoggedInUser();
}

export function resetUser() {
  ["token", "userLevel", "firstTime", "user", "userId"].forEach((k) =>
    localStorage.removeItem(k),
  );
}

// ─── Redirects ────────────────────────────────────────────────────────────────

export function authRedirect() {
  resetUser();
  return <Navigate to="/" replace />;
}

export function defaultRedirection(screen) {
  if (getToken() && getUserId()) {
    if (screen === "verify" || screen === "login") {
      return <Navigate to={getDefaultPathForLevel()} replace />;
    }
  }
  return null;
}
